import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Button,
  Container,
  Form,
  Table,
  Card,
  Row,
  Col,
  Modal,
  ListGroup,
  Spinner,
} from 'react-bootstrap';
import AgreementNameField from '../components/AgreementNameField';
import OperationProgress from '../components/OperationProgress';
import { clearBatchProgress, initBatchProgress, tickBatchProgress } from '../utils/batchProgress';
import { cleanupModalBody, scheduleModalCleanup } from '../utils/modalCleanup';
import './Overview.css';

function IndeterminateCheckbox({
  checked,
  indeterminate,
  onChange,
  onClick,
  className,
  'aria-label': ariaLabel,
}) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      ref={inputRef}
      type="checkbox"
      className={`form-check-input ${className || ''}`.trim()}
      checked={checked}
      onChange={onChange}
      onClick={onClick}
      aria-label={ariaLabel}
    />
  );
}

function emptyForm() {
  return {
    name: '',
    cat: '',
    stock_no: '',
    discount_pct: '',
    discount_price: '',
    special_instructions: '',
  };
}

function itemSelectionKey(catCode, stockNo) {
  return `${catCode}|${stockNo}`;
}

function flattenSalesTree(salesTree) {
  const categories = [];

  for (const node of salesTree || []) {
    if (node.type !== 'sales-category') {
      continue;
    }

    const items = (node.children || [])
      .filter((child) => child.type === 'sales-item')
      .map((child) => ({
        stockNo: child.code,
        name: child.name,
      }));

    categories.push({
      code: node.code,
      name: node.name,
      items,
    });
  }

  return categories.sort((a, b) => a.name.localeCompare(b.name));
}

const SALES_RATE_FIELDS = [
  { field: 'discount_pct', label: 'Discount Pct', min: 1, max: 99, step: '0.01' },
  { field: 'discount_price', label: 'Discount Price', min: 0, step: '0.01' },
];

function emptyRateForm() {
  return {
    discount_pct: '',
    discount_price: '',
    special_instructions: '',
  };
}

function parseRateForm(formData) {
  return {
    discount_pct: formData.discount_pct === '' ? null : formData.discount_pct,
    discount_price: formData.discount_price === '' ? null : formData.discount_price,
    special_instructions: formData.special_instructions || null,
  };
}

function buildQuickCreatePayload(agreementName, catCode, stockNo, rateValues = {}) {
  return {
    name: agreementName.trim(),
    cat: catCode,
    stock_no: stockNo,
    discount_pct: null,
    discount_price: null,
    special_instructions: null,
    ...rateValues,
  };
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function QuickCreateModal({ show, onClose, onCreated, busy = false }) {
  const [agreementName, setAgreementName] = useState('');
  const [categories, setCategories] = useState([]);
  const [loadingTree, setLoadingTree] = useState(false);
  const [treeError, setTreeError] = useState('');
  const [modalError, setModalError] = useState('');
  const [filter, setFilter] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [showRatesModal, setShowRatesModal] = useState(false);
  const [rateForm, setRateForm] = useState(emptyRateForm);
  const submittedRef = useRef(false);

  const handleModalExited = () => {
    if (!show) {
      scheduleModalCleanup();
    }
  };

  useEffect(() => {
    if (!show) {
      setShowRatesModal(false);
      submittedRef.current = false;
      return undefined;
    }

    submittedRef.current = false;
    setShowRatesModal(false);
    setRateForm(emptyRateForm());
    setAgreementName('');
    setFilter('');
    setSelectedKeys(new Set());
    setModalError('');
    setTreeError('');
    setExpandedCategories(new Set());

    let cancelled = false;

    const fetchTree = async () => {
      setLoadingTree(true);
      try {
        const response = await axios.get('/api/overview');
        if (!cancelled) {
          const flattened = flattenSalesTree(response.data?.salesTree || []);
          setCategories(flattened);
          setExpandedCategories(new Set(flattened.map((cat) => cat.code)));
        }
      } catch (err) {
        if (!cancelled) {
          setTreeError(err.response?.data?.error || 'Failed to load sales items');
          setCategories([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingTree(false);
        }
      }
    };

    fetchTree();
    return () => {
      cancelled = true;
    };
  }, [show]);

  const filteredCategories = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) {
      return categories;
    }

    return categories
      .map((cat) => {
        const catMatches =
          cat.code.toLowerCase().includes(query) || cat.name.toLowerCase().includes(query);
        const matchingItems = cat.items.filter((item) => {
          const stockNo = (item.stockNo || '').toLowerCase();
          const name = (item.name || '').toLowerCase();
          return stockNo.includes(query) || name.includes(query);
        });

        if (catMatches) {
          return cat;
        }
        if (matchingItems.length > 0) {
          return { ...cat, items: matchingItems };
        }
        return null;
      })
      .filter(Boolean);
  }, [categories, filter]);

  const selectedCount = selectedKeys.size;

  const getCategorySelectionState = useCallback(
    (cat) => {
      if (cat.items.length === 0) {
        return { checked: false, indeterminate: false };
      }
      const selectedInCategory = cat.items.filter((item) =>
        selectedKeys.has(itemSelectionKey(cat.code, item.stockNo))
      ).length;
      return {
        checked: selectedInCategory === cat.items.length,
        indeterminate: selectedInCategory > 0 && selectedInCategory < cat.items.length,
      };
    },
    [selectedKeys]
  );

  const toggleCategory = (cat, checked) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      for (const item of cat.items) {
        const key = itemSelectionKey(cat.code, item.stockNo);
        if (checked) {
          next.add(key);
        } else {
          next.delete(key);
        }
      }
      return next;
    });
  };

  const toggleItem = (catCode, stockNo, checked) => {
    const key = itemSelectionKey(catCode, stockNo);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const toggleExpanded = (catCode) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(catCode)) {
        next.delete(catCode);
      } else {
        next.add(catCode);
      }
      return next;
    });
  };

  const handleSubmit = () => {
    if (submittedRef.current) {
      return;
    }
    if (!agreementName.trim()) {
      setModalError('Agreement name is required');
      return;
    }
    if (selectedKeys.size === 0) {
      setModalError('Select at least one category or sales item');
      return;
    }

    const selections = [];
    for (const key of selectedKeys) {
      const separatorIndex = key.indexOf('|');
      selections.push({
        catCode: key.slice(0, separatorIndex),
        stockNo: key.slice(separatorIndex + 1),
      });
    }

    setModalError('');
    setRateForm(emptyRateForm());
    setShowRatesModal(true);
  };

  const handleRatesBack = () => {
    submittedRef.current = false;
    setShowRatesModal(false);
  };

  const handleRatesConfirm = () => {
    if (busy) {
      return;
    }

    const selections = [];
    for (const key of selectedKeys) {
      const separatorIndex = key.indexOf('|');
      selections.push({
        catCode: key.slice(0, separatorIndex),
        stockNo: key.slice(separatorIndex + 1),
      });
    }

    onCreated(agreementName, selections, parseRateForm(rateForm));
  };

  const pendingCount = selectedKeys.size;

  return (
    <>
      <Modal
        show={show && !showRatesModal}
        onHide={onClose}
        onExited={handleModalExited}
        size="xl"
        centered
        enforceFocus={false}
        restoreFocus={false}
        style={{ zIndex: 1060 }}
        dialogClassName="hire-rate-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Quick Create Sales Prices</Modal.Title>
        </Modal.Header>
        <Form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <Modal.Body>
            {modalError && <div className="alert alert-danger">{modalError}</div>}
            {treeError && <div className="alert alert-danger">{treeError}</div>}

            <AgreementNameField
              active={show}
              required
              refreshKey="quick-create-sales"
              groupClassName="mb-3 position-relative"
              placeholder="Enter or select agreement name"
              value={agreementName}
              onChange={setAgreementName}
            />

            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <Form.Control
                type="search"
                placeholder="Filter categories or items..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                disabled={loadingTree}
                style={{ maxWidth: '320px' }}
              />
              <Button
                type="button"
                size="sm"
                variant="outline-secondary"
                disabled={loadingTree || categories.length === 0}
                onClick={() => setExpandedCategories(new Set(categories.map((cat) => cat.code)))}
              >
                Expand all
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline-secondary"
                disabled={loadingTree || categories.length === 0}
                onClick={() => setExpandedCategories(new Set())}
              >
                Collapse all
              </Button>
              <span className="text-muted small ms-auto">{selectedCount} item(s) selected</span>
            </div>

            <div
              className="tree-container border rounded quick-create-tree"
              style={{ maxHeight: '50vh' }}
            >
              {loadingTree ? (
                <div className="p-4 text-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading sales items...
                </div>
              ) : filteredCategories.length === 0 ? (
                <div className="p-4 text-muted text-center">No sales items found</div>
              ) : (
                filteredCategories.map((cat) => {
                  const { checked, indeterminate } = getCategorySelectionState(cat);
                  const isExpanded = expandedCategories.has(cat.code) || filter.trim().length > 0;

                  return (
                    <div key={cat.code} className="tree-node">
                      <div
                        className="tree-node-header expandable"
                        style={{ paddingLeft: 8 }}
                        onClick={() => toggleExpanded(cat.code)}
                      >
                        <span className="tree-toggle">{isExpanded ? '▼' : '▶'}</span>
                        <IndeterminateCheckbox
                          checked={checked}
                          indeterminate={indeterminate}
                          onChange={(e) => toggleCategory(cat, e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Select all items in ${cat.name}`}
                          className="me-2 mb-0"
                        />
                        <span className="tree-letter-icon" style={{ backgroundColor: '#8b5cf6' }}>
                          C
                        </span>
                        <span className="tree-code">{cat.code}</span>
                        <span className="tree-name">{cat.name}</span>
                        <span className="tree-count">({cat.items.length})</span>
                      </div>
                      {isExpanded && (
                        <div className="tree-children">
                          {cat.items.length === 0 ? (
                            <div className="text-muted small py-2 ps-5">
                              No items in this category
                            </div>
                          ) : (
                            cat.items.map((item) => {
                              const key = itemSelectionKey(cat.code, item.stockNo);
                              return (
                                <div
                                  key={key}
                                  className="tree-node-header"
                                  style={{ paddingLeft: 32 }}
                                  onClick={() =>
                                    toggleItem(cat.code, item.stockNo, !selectedKeys.has(key))
                                  }
                                >
                                  <span className="tree-toggle-placeholder" />
                                  <IndeterminateCheckbox
                                    checked={selectedKeys.has(key)}
                                    indeterminate={false}
                                    onChange={(e) =>
                                      toggleItem(cat.code, item.stockNo, e.target.checked)
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label={`Select ${item.stockNo}`}
                                    className="me-2 mb-0"
                                  />
                                  <span
                                    className="tree-letter-icon"
                                    style={{ backgroundColor: '#06b6d4' }}
                                  >
                                    S
                                  </span>
                                  <span className="tree-code">{item.stockNo}</span>
                                  <span className="tree-name">{item.name}</span>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loadingTree || selectedCount === 0 || busy}
            >
              Continue
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={show && showRatesModal}
        onHide={busy ? () => {} : handleRatesBack}
        onExited={handleModalExited}
        centered
        enforceFocus={false}
        restoreFocus={false}
        backdropClassName="quick-create-rates-backdrop"
        style={{ zIndex: 1070 }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Set Prices for {pendingCount} item{pendingCount === 1 ? '' : 's'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Values entered here will be applied to all {pendingCount} selected sales price
            {pendingCount === 1 ? '' : 's'}.
          </p>
          <Row>
            {SALES_RATE_FIELDS.map((col) => (
              <Col md={6} key={col.field}>
                <Form.Group className="mb-3">
                  <Form.Label>{col.label}</Form.Label>
                  <Form.Control
                    type="number"
                    name={col.field}
                    min={col.min}
                    max={col.max}
                    step={col.step}
                    value={rateForm[col.field]}
                    onChange={(e) => setRateForm((p) => ({ ...p, [col.field]: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            ))}
          </Row>
          <Form.Group className="mb-0">
            <Form.Label>Special Instructions</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="special_instructions"
              maxLength={2000}
              value={rateForm.special_instructions}
              onChange={(e) => setRateForm((p) => ({ ...p, special_instructions: e.target.value }))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleRatesBack} disabled={busy}>
            Back
          </Button>
          <Button variant="primary" onClick={handleRatesConfirm} disabled={busy}>
            {busy ? 'Creating...' : `Create ${pendingCount} price${pendingCount === 1 ? '' : 's'}`}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

function RecordModal({ record, categories, onClose, onSave, saving }) {
  const [formData, setFormData] = useState(emptyForm());
  const [modalError, setModalError] = useState('');
  const [salesItems, setSalesItems] = useState([]);
  const [stockSearch, setStockSearch] = useState('');
  const [showStockMatches, setShowStockMatches] = useState(false);

  useEffect(() => {
    if (!formData.cat) {
      setSalesItems([]);
      return undefined;
    }

    let cancelled = false;

    const fetchSalesItems = async () => {
      try {
        const response = await axios.get(
          `/api/inclusions/category-items/${encodeURIComponent(formData.cat)}`
        );
        if (!cancelled) {
          setSalesItems(response.data || []);
        }
      } catch {
        if (!cancelled) {
          setSalesItems([]);
        }
      }
    };

    fetchSalesItems();
    return () => {
      cancelled = true;
    };
  }, [formData.cat]);

  useEffect(() => {
    const nextForm = record
      ? {
          name: record.name ?? '',
          cat: record.cat ?? '',
          stock_no: record.stock_no ?? '',
          discount_pct: record.discount_pct ?? '',
          discount_price: record.discount_price ?? '',
          special_instructions: record.special_instructions ?? '',
        }
      : emptyForm();
    setFormData(nextForm);
    setModalError('');
    setShowStockMatches(false);
    setStockSearch('');
  }, [record]);

  useEffect(() => {
    if (!formData.stock_no || salesItems.length === 0) {
      return;
    }
    const match = salesItems.find((item) => item.stock_no === formData.stock_no);
    setStockSearch(match?.name || formData.stock_no);
  }, [formData.stock_no, salesItems]);

  const filteredSalesItems = useMemo(() => {
    const query = stockSearch.trim().toLowerCase();
    if (!query) {
      return [];
    }
    return salesItems
      .filter((item) => {
        const name = (item.name || '').toLowerCase();
        const stockNo = (item.stock_no || '').toLowerCase();
        return name.includes(query) || stockNo.includes(query);
      })
      .slice(0, 20);
  }, [salesItems, stockSearch]);

  const handleCategoryChange = (cat) => {
    setFormData((prev) => ({ ...prev, cat, stock_no: '' }));
    setStockSearch('');
    setShowStockMatches(false);
    setModalError('');
  };

  const handleSelectStock = (item) => {
    setFormData((prev) => ({
      ...prev,
      stock_no: item.stock_no || '',
    }));
    setStockSearch(item.name || item.stock_no || '');
    setShowStockMatches(false);
    setModalError('');
  };

  const handleStockSearchChange = (value) => {
    setStockSearch(value);
    setShowStockMatches(true);
    setFormData((prev) => ({
      ...prev,
      stock_no: value,
    }));
    setModalError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.cat) {
      setModalError('Please select a category');
      return;
    }

    const stockNo = formData.stock_no?.trim() || stockSearch.trim() || null;

    try {
      await onSave({
        name: formData.name.trim(),
        cat: formData.cat,
        stock_no: stockNo,
        discount_pct: formData.discount_pct === '' ? null : formData.discount_pct,
        discount_price: formData.discount_price === '' ? null : formData.discount_price,
        special_instructions: formData.special_instructions || null,
      });
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to save record');
    }
  };

  return (
    <Modal show onHide={onClose} onExited={cleanupModalBody} centered enforceFocus={false}>
      <Modal.Header closeButton>
        <Modal.Title>{record ? 'Edit Sales Price' : 'Add Sales Price'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {modalError && <div className="alert alert-danger">{modalError}</div>}
          <AgreementNameField
            inputName="name"
            active
            required
            refreshKey={record?.id ?? 'new'}
            value={formData.name}
            onChange={(name) => setFormData((p) => ({ ...p, name }))}
          />
          <Form.Group className="mb-3">
            <Form.Label>Cat *</Form.Label>
            <Form.Select
              name="cat"
              value={formData.cat}
              onChange={(e) => handleCategoryChange(e.target.value)}
              required
            >
              <option value="">Select category...</option>
              {categories.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} — {c.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3 position-relative">
            <Form.Label>Stock No</Form.Label>
            <Form.Control
              type="text"
              name="stock_no"
              placeholder={
                formData.cat
                  ? 'Enter stock number or pick from suggestions...'
                  : 'Select a category first'
              }
              value={stockSearch}
              onChange={(e) => handleStockSearchChange(e.target.value)}
              onFocus={() => setShowStockMatches(true)}
              onBlur={() => {
                window.setTimeout(() => setShowStockMatches(false), 150);
              }}
              disabled={!formData.cat}
              autoComplete="off"
            />
            {formData.stock_no && formData.stock_no !== stockSearch && (
              <Form.Text className="text-muted">Stock number saved: {formData.stock_no}</Form.Text>
            )}
            {showStockMatches &&
              formData.cat &&
              stockSearch.trim() &&
              filteredSalesItems.length > 0 && (
                <ListGroup
                  className="position-absolute w-100 shadow-sm"
                  style={{ zIndex: 2, maxHeight: '220px', overflowY: 'auto' }}
                >
                  {filteredSalesItems.map((item) => (
                    <ListGroup.Item
                      key={item.stock_no}
                      action
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => handleSelectStock(item)}
                    >
                      <div className="fw-medium">{item.name}</div>
                      <div className="small text-muted">Stock: {item.stock_no}</div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            {showStockMatches &&
              formData.cat &&
              stockSearch.trim() &&
              filteredSalesItems.length === 0 && (
                <div className="small text-muted mt-1">
                  No matching stock in this category — your entry will be saved as entered
                </div>
              )}
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Discount Pct</Form.Label>
                <Form.Control
                  type="number"
                  name="discount_pct"
                  min={1}
                  max={99}
                  step="0.01"
                  value={formData.discount_pct}
                  onChange={(e) => setFormData((p) => ({ ...p, discount_pct: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Discount Price</Form.Label>
                <Form.Control
                  type="number"
                  name="discount_price"
                  min={0}
                  step="0.01"
                  value={formData.discount_price}
                  onChange={(e) => setFormData((p) => ({ ...p, discount_price: e.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Special Instructions</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="special_instructions"
              maxLength={2000}
              value={formData.special_instructions}
              onChange={(e) => setFormData((p) => ({ ...p, special_instructions: e.target.value }))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function PricingAgreementSalesPrices() {
  const [records, setRecords] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [quickCreating, setQuickCreating] = useState(false);
  const [quickCreateProgress, setQuickCreateProgress] = useState(null);

  const fetchRecords = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const [listRes, categoriesRes] = await Promise.all([
        axios.get('/api/pricing-agreement-sales-prices', { params: { page: 1, limit: 10000 } }),
        axios.get('/api/sales-categories/codes'),
      ]);
      setRecords(asArray(listRes.data?.pricingAgreementSalesPrices));
      setCategories(asArray(categoriesRes.data));
    } catch {
      setError('Failed to fetch records');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchRecords();
    return () => scheduleModalCleanup();
  }, []);

  const closeRecordModal = () => {
    if (saving) {
      return;
    }
    setShowModal(false);
    setEditing(null);
    scheduleModalCleanup();
  };

  const closeQuickCreateModal = () => {
    if (quickCreating) {
      return;
    }
    setShowQuickCreate(false);
    scheduleModalCleanup();
  };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`/api/pricing-agreement-sales-prices/${editing.id}`, payload);
        setSuccess('Record updated successfully');
      } else {
        await axios.post('/api/pricing-agreement-sales-prices', payload);
        setSuccess('Record added successfully');
      }
      setShowModal(false);
      setEditing(null);
      await fetchRecords({ silent: true });
      scheduleModalCleanup();
    } finally {
      setSaving(false);
    }
  };

  const handleQuickCreate = (agreementName, selections, rateValues = {}) => {
    setQuickCreating(true);
    setError('');

    (async () => {
      try {
        let created = 0;
        const failures = [];

        initBatchProgress(setQuickCreateProgress, {
          title: 'Creating sales prices...',
          total: selections.length,
        });

        for (const { catCode, stockNo } of selections) {
          try {
            await axios.post(
              '/api/pricing-agreement-sales-prices',
              buildQuickCreatePayload(agreementName, catCode, stockNo, rateValues)
            );
            created += 1;
            tickBatchProgress(setQuickCreateProgress, { success: true });
          } catch (err) {
            failures.push(
              `${stockNo} (${catCode}): ${err.response?.data?.error || err.message || 'Failed'}`
            );
            tickBatchProgress(setQuickCreateProgress, { failed: true });
          }
        }

        if (created === 0) {
          setError(failures[0] || 'No sales prices were created');
          return;
        }

        setShowQuickCreate(false);
        await fetchRecords({ silent: true });

        if (failures.length > 0) {
          setSuccess(`Created ${created} sales price(s). ${failures.length} failed.`);
          setError(failures.slice(0, 3).join('; '));
        } else {
          setSuccess(`Created ${created} sales price${created === 1 ? '' : 's'} successfully`);
          setError('');
        }
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to create sales prices');
      } finally {
        setQuickCreating(false);
        clearBatchProgress(setQuickCreateProgress);
        scheduleModalCleanup();
      }
    })();
  };

  const handleDeleteAll = async () => {
    if (
      !window.confirm(
        'Are you sure you want to delete ALL sales price records? This action cannot be undone!'
      )
    ) {
      return;
    }
    if (
      !window.confirm(
        'This will permanently delete all your pricing agreement sales price records. Are you absolutely sure?'
      )
    ) {
      return;
    }
    try {
      const response = await axios.delete('/api/pricing-agreement-sales-prices');
      setSuccess(
        `All sales prices deleted successfully (${response.data.deletedCount || 0} records)`
      );
      setError('');
      await fetchRecords({ silent: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete all sales prices');
    }
  };

  return (
    <Container fluid>
      <Row className="mb-2">
        <Col>
          <h1 className="mb-0">Pricing Agreement Sales Prices</h1>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button variant="outline-primary" onClick={() => setShowQuickCreate(true)}>
            Quick Create
          </Button>
          <Button
            onClick={() => {
              setEditing(null);
              setShowModal(true);
            }}
          >
            Add Sales Price
          </Button>
          <Button variant="danger" onClick={handleDeleteAll} disabled={records.length === 0}>
            Delete All
          </Button>
        </Col>
      </Row>
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}
      {quickCreateProgress && (
        <OperationProgress
          title={quickCreateProgress.title}
          processed={quickCreateProgress.processed}
          total={quickCreateProgress.total}
          success={quickCreateProgress.success}
          failed={quickCreateProgress.failed}
          variant="info"
        />
      )}
      <Card>
        <div className="card-body p-0">
          {loading ? (
            <div className="p-4">Loading...</div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Agreement</th>
                  <th>Cat</th>
                  <th>Stock No</th>
                  <th>Discount %</th>
                  <th>Discount Price</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.map((row) => (
                  <tr key={row.id}>
                    <td>{row.name}</td>
                    <td>{row.cat}</td>
                    <td>{row.stock_no || '—'}</td>
                    <td>{row.discount_pct ?? '—'}</td>
                    <td>{row.discount_price ?? '—'}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="me-1"
                        onClick={() => {
                          setEditing(row);
                          setShowModal(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={async () => {
                          if (!window.confirm('Delete this record?')) return;
                          try {
                            await axios.delete(`/api/pricing-agreement-sales-prices/${row.id}`);
                            fetchRecords();
                          } catch (err) {
                            setError(err.response?.data?.error || 'Failed to delete record');
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Card>
      {showModal && (
        <RecordModal
          record={editing}
          categories={categories}
          saving={saving}
          onClose={closeRecordModal}
          onSave={handleSave}
        />
      )}
      {(showQuickCreate || quickCreating) && (
        <QuickCreateModal
          show={showQuickCreate}
          onClose={closeQuickCreateModal}
          onCreated={handleQuickCreate}
          busy={quickCreating}
        />
      )}
    </Container>
  );
}

export default PricingAgreementSalesPrices;
